import useCustomToast from "@/hooks/useCustomToast";
import { useColorMode } from "@chakra-ui/color-mode";
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Icon,
  Text,
  VStack,
  Spinner,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiMaximize2,
  FiMinimize2,
  FiUpload,
  FiX,
} from "react-icons/fi";
import { type IHighlight, PdfHighlighter } from "react-pdf-highlighter";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker?url";
import "react-pdf-highlighter/dist/style.css";
import { useDocumentStore, type PdfFileData, type FileType } from "../../stores/documents";
import type { IconType } from "react-icons/lib";
import { FiFile } from "react-icons/fi";

const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "text/csv": [".csv"],
};

const getFileType = (fileName: string): FileType => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "pdf";
  if (ext === "xlsx") return "xlsx";
  if (ext === "csv") return "csv";
  return "unknown";
};

const isAcceptedFileType = (file: File): boolean => {
  const ext = file.name.split(".").pop()?.toLowerCase();
  return ext === "pdf" || ext === "xlsx" || ext === "csv";
};
//import * as pdfMake from "pdfmake/build/pdfmake";
//import type { TDocumentDefinitions } from "pdfmake/interfaces";

// PDF.js Worker 설정
GlobalWorkerOptions.workerSrc = workerSrc;

interface SampleDocument {
  id: string;
  name: string;
  icon: IconType;
  template?: string;
}

interface DocumentUploaderProps {
  onTemplateLoad?: (templateName: string) => void;
  onReset?: () => void;
}

const SAMPLE_DOCUMENTS: SampleDocument[] = [
  {
    id: "lab_report",
    name: "실험보고서",
    icon: FiFileText,
    template: "lab_report",
  },
  {
    id: "meeting_minutes",
    name: "회의록",
    icon: FiFileText,
    template: "meeting_minutes",
  },
  {
    id: "result_report",
    name: "결과보고서",
    icon: FiFileText,
    template: "result_report",
  },
];

const DocumentUploader = ({
  onTemplateLoad,
  onReset,
}: DocumentUploaderProps) => {
  const { colorMode } = useColorMode();
  const { showSuccessToast, showErrorToast } = useCustomToast();
  const [pdfViewerKey, setPdfViewerKey] = useState(0);
  const [isViewerReady, setIsViewerReady] = useState(false);
  const loadingRef = useRef<boolean>(false);

  const {
    pdfFile,
    pdfDocument,
    currentPage,
    totalPages,
    isDocumentLoaded,
    fileSize,
    setCurrentPage,
    setPdfFile,
    setPdfDocument,
    setTotalPages,
    setIsDocumentLoaded,
    setPdfError,
    setFileSize,
    fileType,
    setFileType,
  } = useDocumentStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const highlighterRef = useRef<PdfHighlighter<IHighlight>>(null);
  const scrollToRef = useRef<(highlight: IHighlight) => void>(() => {});

  const hasReloadedRef = useRef<boolean>(false);

  const resetPdfStateOnly = useCallback(() => {
    setPdfFile(null);
    setPdfDocument(null);
    setPdfError(null);
    setIsDocumentLoaded(false);
    setIsViewerReady(false);
    setPdfViewerKey(0);
    setFileSize(0);
    setFileType("pdf");
    localStorage.removeItem("pdfFile");
    localStorage.removeItem("pdf-document-storage");
  }, [
    setPdfFile,
    setPdfDocument,
    setPdfError,
    setIsDocumentLoaded,
    setFileSize,
    setFileType,
  ]);

  const resetPdfState = useCallback(() => {
    resetPdfStateOnly();
    // 외부 reset 콜백 호출 (실제 리셋 버튼 클릭 시에만 사용)
    if (onReset) {
      onReset();
    }
  }, [resetPdfStateOnly, onReset]);

  const handleFileSelected = useCallback(
    async (file: File) => {
      if (!file || !isAcceptedFileType(file) || loadingRef.current) {
        return;
      }

      try {
        loadingRef.current = true;
        resetPdfStateOnly();
        setFileSize(file.size);

        const detectedFileType = getFileType(file.name);
        setFileType(detectedFileType);

        // base64로 변환하여 저장
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          setPdfFile({
            data: base64,
            name: file.name,
          } as PdfFileData);
        };
        reader.readAsDataURL(file);

        if (detectedFileType === "pdf") {
          // PDF: 기존 로직
          const buffer = await file.arrayBuffer();
          const pdfDoc = await getDocument({ data: buffer }).promise;
          await pdfDoc.getPage(1);
          setPdfDocument(pdfDoc);
          setTotalPages(pdfDoc.numPages);
        }

        setIsDocumentLoaded(true);
      } catch (error) {
        console.error("파일 로드 실패:", error);
        setPdfError(
          error instanceof Error ? error.message : "Failed to load file"
        );
        resetPdfStateOnly();
      } finally {
        loadingRef.current = false;
      }
    },
    [
      resetPdfStateOnly,
      setPdfFile,
      setPdfDocument,
      setTotalPages,
      setPdfError,
      setIsDocumentLoaded,
      setFileSize,
      setFileType,
    ]
  );

//  const textToImage = (
//    text: string,
//    fontSize: number,
//    isBold: boolean = false,
//    width: number = 800
//  ): Promise<string> => {
//    return new Promise((resolve) => {
//      const canvas = document.createElement("canvas");
//      const ctx = canvas.getContext("2d");
//      if (!ctx) {
//        resolve("");
//        return;
//      }
//
//      canvas.width = width;
//      canvas.height = 100;
//
//      // 한글 폰트 설정 (시스템 기본 한글 폰트 사용)
//      ctx.font = `${isBold ? "bold " : ""}${fontSize}px "Malgun Gothic", "AppleGothic", "NanumGothic", sans-serif`;
//      ctx.fillStyle = "#000000";
//      ctx.textBaseline = "top";
//
//      // 텍스트를 여러 줄로 나누기
//      const lines: string[] = [];
//      const words = text.split(" ");
//      let currentLine = "";
//
//      for (const word of words) {
//        const testLine = currentLine ? `${currentLine} ${word}` : word;
//        const metrics = ctx.measureText(testLine);
//        if (metrics.width > width - 40 && currentLine) {
//          lines.push(currentLine);
//          currentLine = word;
//        } else {
//          currentLine = testLine;
//        }
//      }
//      if (currentLine) {
//        lines.push(currentLine);
//      }
//
//      // 캔버스 높이 조정
//      canvas.height = Math.max(100, lines.length * (fontSize + 10) + 20);
//
//      // 배경을 흰색으로
//      ctx.fillStyle = "#FFFFFF";
//      ctx.fillRect(0, 0, canvas.width, canvas.height);
//
//      // 텍스트 다시 그리기
//      ctx.fillStyle = "#000000";
//      ctx.font = `${isBold ? "bold " : ""}${fontSize}px "Malgun Gothic", "AppleGothic", "NanumGothic", sans-serif`;
//      lines.forEach((line, index) => {
//        ctx.fillText(line, 20, 10 + index * (fontSize + 10));
//      });
//
//      resolve(canvas.toDataURL("image/png"));
//    });
//  };

//  const generateMeetingMinutesPDF = async (): Promise<Blob> => {
//    // pdfmake 폰트 설정
//    try {
//      const pdfFonts = await import("pdfmake/build/vfs_fonts");
//      pdfMake.vfs = pdfFonts.default || pdfFonts;
//    } catch (e) {
//      console.warn("vfs_fonts를 로드할 수 없습니다.");
//    }
//
//    const today = new Date();
//    const dateStr = `${today.getFullYear()}년 ${today.getMonth() + 1}월 ${today.getDate()}일`;
//
//    // 한글 텍스트를 이미지로 변환
//    const titleImage = await textToImage("조선소 블록 제작 공정 회의록", 18, true);
//    const dateImage = await textToImage(`회의 일시: ${dateStr}`, 12);
//    const locationImage = await textToImage("회의 장소: 조선소 본관 회의실", 12);
//    const subjectImage = await textToImage("회의 주제: 블록 제작 공정 개선 방안", 12);
//    const participantsTitleImage = await textToImage("참석자", 14, true);
//    const participantsImage = await textToImage(
//      "• 생산팀장: 홍길동\n• 공정팀장: 김철수\n• 품질팀장: 이영희\n• 설계팀장: 박민수",
//      12
//    );
//    const contentTitleImage = await textToImage("회의 내용", 14, true);
//    const contentImage = await textToImage(
//      "1. 블록 제작 공정 현황\n   • 현재 블록 제작 공정은 총 5단계로 구성되어 있습니다.\n   • 각 단계별 소요 시간: 절단(2일) → 용접(5일) → 조립(7일) → 검사(2일) → 출하(1일)\n\n2. 공정 개선 방안\n   • 용접 공정 자동화 도입으로 소요 시간 30% 단축 예상\n   • 품질 검사 프로세스 개선으로 불량률 감소 목표\n   • 조립 공정 표준화를 통한 효율성 향상\n\n3. 향후 일정\n   • 다음 주: 자동화 장비 도입 검토\n   • 다음 달: 표준화 프로세스 문서화\n   • 분기 말: 개선 효과 측정 및 평가",
//      12
//    );
//    const conclusionTitleImage = await textToImage("결론", 14, true);
//    const conclusionImage = await textToImage(
//      "블록 제작 공정 개선을 위해 자동화 도입 및 프로세스 표준화를 추진하기로 결정했습니다.",
//      12
//    );
//    const writerImage = await textToImage("작성자: 생산팀장 홍길동", 12);
//    const approverImage = await textToImage("승인: 공정팀장 김철수", 12);
//
//    const docDefinition: TDocumentDefinitions = {
//      content: [
//        { image: titleImage, width: 500, alignment: "center", margin: [0, 0, 0, 20] },
//        { image: dateImage, width: 500, margin: [0, 0, 0, 5] },
//        { image: locationImage, width: 500, margin: [0, 0, 0, 5] },
//        { image: subjectImage, width: 500, margin: [0, 0, 0, 15] },
//        { image: participantsTitleImage, width: 500, margin: [0, 0, 0, 8] },
//        { image: participantsImage, width: 500, margin: [20, 0, 0, 15] },
//        { image: contentTitleImage, width: 500, margin: [0, 0, 0, 8] },
//        { image: contentImage, width: 500, margin: [20, 0, 0, 15] },
//        { image: conclusionTitleImage, width: 500, margin: [0, 0, 0, 8] },
//        { image: conclusionImage, width: 500, margin: [0, 0, 0, 20] },
//        {
//          columns: [
//            { text: "", width: "*" },
//            {
//              width: "auto",
//              stack: [
//                { image: writerImage, width: 200, alignment: "right", margin: [0, 0, 0, 5] },
//                { image: approverImage, width: 200, alignment: "right" },
//              ],
//            },
//          ],
//        },
//      ],
//      pageSize: "A4",
//      pageMargins: [40, 60, 40, 60],
//    };
//
//    return new Promise((resolve, reject) => {
//      try {
//        const pdfDoc = pdfMake.createPdf(docDefinition);
//        pdfDoc.getBlob((blob: Blob) => {
//          resolve(blob);
//        });
//      } catch (error) {
//        reject(error);
//      }
//    });
//  };

  const handleLoadTemplate = async (templateName: string) => {
    try {
      loadingRef.current = true;
      resetPdfStateOnly();
      setIsViewerReady(false);

      let pdfBlob: Blob;

      // 회의록인 경우 meeting_minuts.pdf 파일을 불러옴
      const pdfFileName = templateName === "meeting_minutes" ? "meeting_minuts" : templateName;
      
      // 템플릿 PDF 파일 로드
      const pdfResponse = await fetch(
        `/assets/templates/examples/${pdfFileName}.pdf`
      );
      if (!pdfResponse.ok) {
        throw new Error(`PDF 파일을 찾을 수 없습니다: ${pdfFileName}.pdf`);
      }
      pdfBlob = await pdfResponse.blob();

      setFileSize(pdfBlob.size);

      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(pdfBlob);
      });

      setPdfFile({
        data: base64,
        name: `${templateName}.pdf`,
      } as PdfFileData);

      // PDF 문서 로드
      const buffer = await pdfBlob.arrayBuffer();
      const pdfDoc = await getDocument({ data: buffer }).promise;
      setPdfDocument(pdfDoc);
      setTotalPages(pdfDoc.numPages);
      setIsDocumentLoaded(true);

      // 샘플 스키마 로드
      try {
        const schemaUrl = `/assets/templates/${templateName}.json`;
        const schemaResponse = await fetch(schemaUrl);

        if (schemaResponse.ok) {
          const schemaData = await schemaResponse.json();

          // 스키마를 localStorage에 저장
          localStorage.setItem("sampleSchema", JSON.stringify(schemaData));

          showSuccessToast(
            `${templateName} 템플릿과 스키마가 성공적으로 로드되었습니다.`
          );

          // 스키마 로드 완료 후 즉시 콜백 호출
          if (onTemplateLoad) {
            onTemplateLoad(templateName);
          }
        } else {
          showSuccessToast(
            `${templateName} 템플릿이 성공적으로 로드되었습니다.`
          );

          // 스키마가 없어도 콜백 호출
          if (onTemplateLoad) {
            onTemplateLoad(templateName);
          }
        }
      } catch (schemaError) {
        showSuccessToast(`${templateName} 템플릿이 성공적으로 로드되었습니다.`);

        // 스키마 로드 실패해도 콜백 호출
        if (onTemplateLoad) {
          onTemplateLoad(templateName);
        }
      }
    } catch (error) {
      console.error("템플릿 로드 실패:", error);

      // PDF 로드 실패인지 스키마 로드 실패인지 구분
      if (error instanceof Error && error.message.includes("PDF")) {
        showErrorToast(error.message);
      } else {
        showErrorToast("템플릿을 로드하는 중 오류가 발생했습니다.");
      }
      resetPdfStateOnly();
    } finally {
      loadingRef.current = false;
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        handleFileSelected(acceptedFiles[0]);
      }
    },
    [handleFileSelected]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxFiles: 1,
    disabled: loadingRef.current,
  });

  // PDF 뷰어 초기화 effect (PDF 파일일 때만)
  useEffect(() => {
    if (!pdfDocument || !isDocumentLoaded || fileType !== "pdf") {
      setIsViewerReady(false);
      return;
    }

    const initializeViewer = async () => {
      try {
        setIsViewerReady(false);

        // PDF 문서가 완전히 로드될 때까지 더 오래 대기
        await new Promise((resolve) => setTimeout(resolve, 500));

        // 첫 번째 페이지에 접근하여 문서가 완전히 로드되었는지 확인
        if (pdfDocument.numPages > 0) {
          const page = await pdfDocument.getPage(1);
          // 페이지 뷰포트를 가져와서 문서가 완전히 로드되었는지 확인
          await page.getViewport({ scale: 1.0 });
        }

        // 컨테이너가 DOM에 마운트되었는지 확인
        if (!containerRef.current) {
          return;
        }

        // 추가 대기 시간으로 PDF 뷰어가 완전히 준비되도록 함
        await new Promise((resolve) => setTimeout(resolve, 300));

        setIsViewerReady(true);
        setPdfViewerKey((prev) => prev + 1);
      } catch (error) {
        console.error("PDF 뷰어 초기화 실패:", error);
        setIsViewerReady(false);
        setPdfError("PDF 뷰어 초기화에 실패했습니다.");
      }
    };

    initializeViewer();
  }, [pdfDocument, isDocumentLoaded, setPdfError]);

  // 재접속 시 저장된 파일 복원
  useEffect(() => {
    const reloadFromStoredData = async () => {
      if (loadingRef.current || !pdfFile) return;
      if (hasReloadedRef.current) return;

      // 비 PDF 파일은 문서 로드만 표시
      if (fileType !== "pdf") {
        if (!isDocumentLoaded) {
          hasReloadedRef.current = true;

          // 파일 크기 계산
          if (typeof pdfFile.data === "string" && fileSize === 0) {
            try {
              const base64Data = pdfFile.data.split(",")[1];
              const binaryString = atob(base64Data);
              setFileSize(binaryString.length);
            } catch { /* ignore */ }
          }

          setIsDocumentLoaded(true);
        }
        return;
      }

      // PDF 재로드
      if (pdfDocument) return;

      try {
        hasReloadedRef.current = true;
        loadingRef.current = true;
        setIsViewerReady(false);

        let buffer: ArrayBuffer;
        let calculatedSize: number;

        if (typeof pdfFile.data === "string") {
          const base64Data = pdfFile.data.split(",")[1];
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          buffer = bytes.buffer;
          calculatedSize = binaryString.length;
        } else {
          buffer = await pdfFile.data.arrayBuffer();
          calculatedSize = buffer.byteLength;
        }

        setFileSize(calculatedSize);

        const pdfDoc = await getDocument({ data: buffer }).promise;
        await pdfDoc.getPage(1);

        setPdfDocument(pdfDoc);
        setTotalPages(pdfDoc.numPages);
        setIsDocumentLoaded(true);
      } catch (error) {
        setPdfError(
          error instanceof Error ? error.message : "Failed to reload PDF"
        );
        setPdfFile(null);
        setFileSize(0);
      } finally {
        loadingRef.current = false;
      }
    };

    reloadFromStoredData();
  }, [
    pdfFile,
    pdfDocument,
    fileType,
    fileSize,
    isDocumentLoaded,
    setPdfDocument,
    setTotalPages,
    setIsDocumentLoaded,
    setPdfError,
    setPdfFile,
    setFileSize,
  ]);

  // Scroll handling for PDF viewer
  useEffect(() => {
    if (!isDocumentLoaded || !containerRef.current) {
      return;
    }

    const handleScroll = () => {
      requestAnimationFrame(() => {
        const container = containerRef.current;
        if (!container) return;

        // offsetParent가 설정되었는지 확인
        const pages = Array.from(container.querySelectorAll(".page")).filter(
          (page): page is HTMLElement => {
            if (!page || !(page instanceof HTMLElement)) return false;
            // offsetParent가 설정되었는지 확인
            return page.offsetParent !== null;
          }
        );

        if (!pages.length) {
          return;
        }

        const containerRect = container.getBoundingClientRect();
        const containerTop = containerRect.top;
        const containerHeight = containerRect.height;
        let maxVisibleRatio = 0;
        let mostVisiblePage = pages[0];

        for (const page of pages) {
          const pageRect = page.getBoundingClientRect();
          const visibleTop = Math.max(pageRect.top, containerTop);
          const visibleBottom = Math.min(
            pageRect.bottom,
            containerTop + containerHeight
          );
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          const visibleRatio = visibleHeight / pageRect.height;

          if (visibleRatio > maxVisibleRatio) {
            maxVisibleRatio = visibleRatio;
            mostVisiblePage = page;
          }
        }
        const pageNumber = pages.indexOf(mostVisiblePage) + 1;
        if (
          pageNumber !== currentPage &&
          pageNumber > 0 &&
          pageNumber <= totalPages
        ) {
          setCurrentPage(pageNumber);
        }
      });
    };

    let timeoutId: NodeJS.Timeout;
    const debouncedHandleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100);
    };

    containerRef.current?.addEventListener(
      "scroll",
      debouncedHandleScroll,
      true
    );

    // 초기 스크롤 핸들링을 지연시켜 DOM이 완전히 준비되도록 함
    const initTimeout = setTimeout(() => {
      if (containerRef.current) {
        handleScroll();
      }
    }, 1500);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(initTimeout);
      containerRef.current?.removeEventListener(
        "scroll",
        debouncedHandleScroll,
        true
      );
    };
  }, [currentPage, totalPages, isDocumentLoaded, setCurrentPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      const container = containerRef.current;
      if (container) {
        const pages = Array.from(
          container.getElementsByClassName("page")
        ).filter(
          (page): page is HTMLElement =>
            page instanceof HTMLElement && page.offsetParent !== null
        );
        if (pages.length >= newPage) {
          const targetPage = pages[newPage - 1];
          if (targetPage) {
            // 스크롤 전에 약간의 지연을 두어 DOM이 안정화되도록 함
            setTimeout(() => {
              targetPage.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
              });
            }, 100);
          }
        }
      }
    }
  };

  const renderFilePreview = () => {
    // 비 PDF 파일 프리뷰
    if (fileType !== "pdf") {
      const ext = fileType.toUpperCase();
      return (
        <Flex
          justify="center"
          align="center"
          h="100%"
          direction="column"
          gap={4}
          color="gray.500"
        >
          <Icon as={FiFile} w={20} h={20} color="blue.400" />
          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            {ext} 파일
          </Text>
          <Text fontSize="sm" color="gray.500">
            {pdfFile?.name}
          </Text>
          <Text fontSize="xs" color="gray.400">
            미리보기는 PDF 파일에서만 지원됩니다.
          </Text>
        </Flex>
      );
    }

    // PDF 파일이 있지만 문서가 로드되지 않은 경우 (재접속 시나리오)
    if (pdfFile && !pdfDocument && !isDocumentLoaded) {
      return (
        <Flex justify="center" align="center" h="100%" color="gray.500">
          <Spinner mr={2} />
          <Text>PDF 문서 복원 중...</Text>
        </Flex>
      );
    }

    if (
      loadingRef.current ||
      !pdfDocument ||
      !isDocumentLoaded ||
      !isViewerReady ||
      totalPages === 0
    ) {
      return (
        <Flex justify="center" align="center" h="100%" color="gray.500">
          <Spinner mr={2} />
          <Text>PDF 로딩 중...</Text>
        </Flex>
      );
    }

    if (!containerRef.current) {
      return (
        <Flex justify="center" align="center" h="100%" color="gray.500">
          <Spinner mr={2} />
          <Text>PDF 뷰어 초기화 중...</Text>
        </Flex>
      );
    }

    return (
      <PdfHighlighter
        key={`pdf-highlighter-${pdfViewerKey}`}
        ref={highlighterRef}
        pdfDocument={pdfDocument as any}
        enableAreaSelection={() => false}
        highlights={[]}
        scrollRef={(scrollTo) => {
          scrollToRef.current = scrollTo;
        }}
        onScrollChange={() => {}}
        highlightTransform={() => <div />}
        onSelectionFinished={() => <div />}
      />
    );
  };

  return (
    <Box
      flex={1}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      height="100%"
    >
      <Box>
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          문서 업로드
        </Text>
      </Box>
      {!pdfFile ? (
        <Box
          {...getRootProps()}
          borderWidth={2}
          borderStyle="dashed"
          borderColor={colorMode === "light" ? "gray.200" : "gray.700"}
          borderRadius="lg"
          flex={1}
          display="flex"
          flexDir="column"
          justifyContent="center"
          alignItems="center"
          bg={
            isDragActive
              ? colorMode === "light"
                ? "gray.50"
                : "gray.700"
              : "transparent"
          }
          transition="all 0.2s"
          cursor={loadingRef.current ? "default" : "pointer"}
          minH="400px"
        >
          <input {...getInputProps()} />
          {loadingRef.current ? (
            <Spinner size="xl" />
          ) : (
            <>
              <Icon
                as={FiUpload}
                w={16}
                h={16}
                mb={6}
                color={colorMode === "light" ? "gray.400" : "gray.500"}
              />
              <Text fontWeight="medium" fontSize="lg">
                문서 업로드
              </Text>
              <Text fontSize="sm" mt={2}>
                드래그 앤 드롭 또는 클릭하여 선택
              </Text>
              <Text fontSize="xs" mt={1} color="gray.400">
                PDF, XLSX, CSV 파일 지원
              </Text>
              <VStack mt={8} gap={4} align="stretch" w="full" maxW="sm">
                <Text fontSize="sm" textAlign="center">
                  샘플 템플릿 사용
                </Text>
                <HStack gap={2} justify="center">
                  {SAMPLE_DOCUMENTS.map((doc) => (
                    <Button
                      key={doc.id}
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (doc.template) {
                          handleLoadTemplate(doc.template);
                        } else {
                          showSuccessToast(
                            `${doc.name} 템플릿이 곧 제공될 예정입니다.`
                          );
                        }
                      }}
                      disabled={loadingRef.current}
                    >
                      <Icon as={doc.icon} mr={2} />
                      {doc.name}
                    </Button>
                  ))}
                </HStack>
              </VStack>
            </>
          )}
        </Box>
      ) : (
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          borderRadius="lg"
          overflow="hidden"
          height="100%"
        >
          <Flex
            p={4}
            justifyContent="space-between"
            alignItems="center"
            bg="transparent"
            shrink={0}
          >
            <HStack gap={2}>
              <Icon as={FiFileText} />
              <Text
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {pdfFile.name}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {(() => {
                  // 파일 크기 계산 함수
                  const calculateFileSize = () => {
                    if (fileSize > 0) {
                      return (fileSize / (1024 * 1024)).toFixed(2);
                    }

                    // pdfFile이 있지만 fileSize가 0인 경우 (재접속 시나리오)
                    if (pdfFile && typeof pdfFile.data === "string") {
                      try {
                        // base64 데이터에서 파일 크기 계산
                        const base64Data = pdfFile.data.split(",")[1]; // data:application/pdf;base64, 부분 제거
                        const binaryString = atob(base64Data);
                        const calculatedSize = binaryString.length;
                        return (calculatedSize / (1024 * 1024)).toFixed(2);
                      } catch (error) {
                        return "알 수 없음";
                      }
                    }

                    return "알 수 없음";
                  };

                  const sizeText = calculateFileSize();
                  return `(${sizeText}${sizeText !== "알 수 없음" ? " MB" : ""})`;
                })()}
              </Text>
            </HStack>
            <Button
              size="sm"
              variant="ghost"
              onClick={resetPdfState}
              disabled={loadingRef.current}
            >
              <Icon as={FiX} />
            </Button>
          </Flex>
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            position="relative"
            borderWidth={1}
            borderColor={colorMode === "light" ? "gray.200" : "gray.700"}
            borderRadius="md"
            height="100%"
            minHeight={0}
          >
            {fileType === "pdf" && (
            <Flex
              py={2}
              px={4}
              borderBottomWidth={1}
              borderColor={colorMode === "light" ? "gray.200" : "gray.700"}
              justifyContent="space-between"
              alignItems="center"
              shrink={0}
            >
              <HStack gap={4} flex={1} justify="center">
                <Button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1 || loadingRef.current}
                  size="sm"
                  variant="outline"
                >
                  <Icon as={FiChevronLeft} />
                </Button>
                <Text>
                  {currentPage} / {totalPages}
                </Text>
                <Button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages || loadingRef.current}
                  size="sm"
                  variant="outline"
                >
                  <Icon as={FiChevronRight} />
                </Button>
              </HStack>
              <ButtonGroup size="sm" variant="outline">
                <Button
                  onClick={() => {
                    if (highlighterRef.current?.viewer) {
                      highlighterRef.current.viewer.currentScaleValue =
                        "page-fit";
                    }
                  }}
                  disabled={loadingRef.current}
                >
                  <Icon as={FiMaximize2} />
                </Button>
                <Button
                  onClick={() => {
                    if (highlighterRef.current?.viewer) {
                      highlighterRef.current.viewer.currentScaleValue =
                        "page-width";
                    }
                  }}
                  disabled={loadingRef.current}
                >
                  <Icon as={FiMinimize2} />
                </Button>
              </ButtonGroup>
            </Flex>
            )}
            <Box flex={1} position="relative" overflow="hidden">
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="auto"
                ref={containerRef}
                className="pdf-container"
                css={{
                  scrollBehavior: "smooth",
                  "& > div": {
                    position: "relative",
                    height: "100%",
                    width: "100%",
                  },
                  ".PdfHighlighter": {
                    position: "relative",
                    height: "100%",
                    width: "100%",
                    "& > div": {
                      position: "relative",
                      backgroundColor: "white",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                  },
                  ".page": {
                    margin: "0 auto",
                    position: "relative",
                    backgroundColor: "white",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    width: "100%",
                    maxWidth: "800px",
                    "& > canvas": {
                      width: "100% !important",
                      height: "auto !important",
                      display: "block",
                    },
                  },
                  ".textLayer": {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.2,
                    mixBlendMode: "multiply",
                    width: "100%",
                    pointerEvents: "none",
                  },
                  ".annotationLayer": {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: "100%",
                    pointerEvents: "none",
                  },
                }}
              >
                {renderFilePreview()}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DocumentUploader;
